/* ============================================================
 * SOS Map Navigation � Vanilla JS (no jQuery)
 * ============================================================ */

/* ------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

/* ------------------------------------------------------------
 * Global config holder
 * ------------------------------------------------------------ */
const _config = {
    static: {},
    dynamic: {
        initCount: 0,
        initMax: 20,
        timeout: [],
        apiStatic: null,
        apiData: {
            raw: null,
            continents: {},
            countries: {},
            villages: {},
            projects: {}
        },
        map: null,
        state: { name: 'world', id: null }
    },
    cluster: {
        country: {},
        village: {}
    }
};

/* ============================================================
 * Core
 * ============================================================ */
const Core = (() => {

    const timeout = (fn, delay) => {
        const t = setTimeout(fn, delay);
        _config.dynamic.timeout.push(t);
        return t;
    };

    const getJson = (url, success, always = () => { }, fail = () => { }) => {
        fetch(url, { credentials: 'same-origin' })
            .then(r => {
                if (!r.ok) throw new Error(r.statusText);
                return r.json();
            })
            .then(success)
            .catch(fail)
            .finally(always);
    };

    const isTouchDevice = () =>
        'ontouchstart' in window;

    const isInMobileView = () => {
        const el = $(_config.static.domIds.contentContainerMain);
        return el ? el.getBoundingClientRect().width < 970 : false;
    };

    const simpleDepictionNeeded = () => {
        const m = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
        if (!m) return false;
        return Number(`${m[1]}.${m[2]}.${m[3] || 0}`) <= 7;
    };

    const getStatic = () => {
        getJson(_config.static.staticUrl, data => {
            _config.static.apiStatic = data;
            _config.dynamic.apiStatic = data;
            getDynamic();
        });
    };

    const getDynamic = () => {
        getJson(_config.static.dynamicUrl, data => {
            _config.dynamic.apiData.raw = data;
            _config.dynamic.apiData.continents = data.continents || {};
            _config.dynamic.apiData.countries = data.countries || {};
            _config.dynamic.apiData.villages = data.villages || {};
            _config.dynamic.apiData.projects = data.projects || {};

            if (SOSMapConfig.hideVillages.toLowerCase() === "true")
                for (var v in _config.dynamic.apiData.villages) {
                    var village = _config.dynamic.apiData.villages[v];
                    var i = village.url.lastIndexOf("/");
                    var vName = village.url.substring(i + 1);
                    village.url = village.url.substring(0, i) + "?v=" + vName
            }

            SOSMap.init();
        });
    };

    const getConfig = () => {
        if (window.vz?.sos?.config?.MapNavigation) {
            _config.static = vz.sos.config.MapNavigation;
            return true;
        }
        _config.dynamic.initCount++;
        if (_config.dynamic.initCount < _config.dynamic.initMax) {
            timeout(init, 500);
        }
        return false;
    };

    const isDefined = function (value) {
        return typeof value !== 'undefined';
    };

    const init = () => {
        if (getConfig()) getStatic();
    };

    return {
        init,
        getJson,
        isDefined,
        isTouchDevice,
        isInMobileView,
        simpleDepictionNeeded
    };
})();

/* ============================================================
 * SOSMap (Renamed from Map to avoid collision)
 * ============================================================ */
const SOSMap = (() => {

    const setMap = map => (_config.dynamic.map = map);
    const getMap = () => _config.dynamic.map;
    const getMapElement = () => document.querySelector(_config.static.domIds.map.canvas);

    const extendGoogleMaps = () => {

        google.maps.Marker.prototype.setLabel = function () {
            this.label = new MarkerLabel({ marker: this });
            this.label.bindTo('position', this, 'position');
        };

        google.maps.Marker.prototype.addToMap = function (map) {
            this.setMap(map);
            if (this.label) this.label.setMap(map);
        };

        class MarkerLabel extends google.maps.OverlayView {
            constructor(opts) {
                super();
                this.setValues(opts);
                this.el = document.createElement('span');
            }

            onAdd() {
                this.el.className = this.marker.labelClass;
                this.el.innerHTML = `
                    <span class="title">${this.marker.labelTitle}</span>
                    ${this.marker.labelNumber ? `<span class="ncount">${this.marker.labelNumber}</span>` : ''}
                `;
                this.el.style.width = `${this.marker.size.x}px`;
                this.el.style.height = `${this.marker.size.y}px`;
                this.getPanes().overlayImage.appendChild(this.el);
            }

            draw() {
                const p = this.getProjection().fromLatLngToDivPixel(this.get('position'));
                this.el.style.left = `${p.x - this.marker.size.x / 2}px`;
                this.el.style.top = `${p.y - this.marker.size.y}px`;
            }

            onRemove() {
                this.el.remove();
            }
        }
    };

    const createMapCanvasDom = function () {
        // Create the parent container
        const parent = document.createElement('div');
        parent.id = _config.static.domIds.map.main.replace(/#/, '');
        parent.classList.add('mapNavMapContainer');

        // Create the child canvas element
        const child = document.createElement('div');
        child.id = _config.static.domIds.map.canvas.replace(/#/, '');
        child.classList.add('mapNavMap');

        // Nest the elements
        parent.appendChild(child);

        // Append to the main container defined in your config
        const mainContainer = $(_config.static.domIds.main);
        if (mainContainer) {
            mainContainer.appendChild(parent);
        }
    };

    const getMapStyle = function (type, feature) {
        return _config.static.mapDataLayerStyles[type][feature]
    };

    const setMapDataLayerDefaultStyle = function () {
        _config.dynamic.map.data.setStyle(function (feature) {
            var configStyle;
            if (feature.getProperty('type') == 'continent') {
                configStyle = getMapStyle('normal', 'continent');
            } else {
                configStyle = getMapStyle('normal', 'country');
                if (feature.getProperty('isActive') == true) {
                    configStyle = getMapStyle('active', 'country');
                }
            }

            configStyle.visible = feature.getProperty('isVisible') == true;
            return configStyle;
        });
    };

    const mapDataLayerMouseout = function (event) {
        getMap().data.revertStyle();
        Navigation.onDataLayerOut();

        if (event) {
            var feature = event.feature;
            var id = feature.getProperty('id');
            if (feature.getProperty('type') == 'country') {
                setCountryMarkerVisible(id, false);
            }
        }
    };

    const setCountryMarkerVisible = function (id, status) {
        var mrk = _config.cluster.country[id];
        if (mrk) mrk.addToMap(status == false ? null : SOSMap.getMap());
    };

    const updateDataLayers = function () {
        if (Core.isTouchDevice()) mapDataLayerMouseout();

        var stateName = _config.dynamic.state.name;
        var mapData = getMap().data;
        var curCountry = Countries.getCurrent();

        if (stateName === 'world') {
            mapData.forEach(function (feature) {
                feature.setProperty('isVisible', true);
                feature.setProperty('isActive', false);
            });
        } else if (stateName === 'village' || stateName === 'project') {
            mapData.forEach(function (feature) {
                feature.setProperty('isVisible', false);
            });
        } else {
            mapData.forEach(function (feature) {
                if (feature.getProperty('type') === 'continent') {
                    feature.setProperty('isVisible', false);
                } else {
                    feature.setProperty('isVisible', true);

                    if (stateName == 'continent') {
                        feature.setProperty('isActive', false);
                    } else {
                        if (curCountry == feature.getProperty('id') && stateName === 'country') {
                            feature.setProperty('isActive', true);
                        } else {
                            feature.setProperty('isActive', false);
                        }
                    }
                }
            });
        }
    };

    const stateChange = function (newState, id) {
        setZoomToDom();

        document.querySelectorAll(`${_config.static.domIds.main} .activeOver`)
            .forEach(el => el.classList.remove("activeOver"));

        _config.dynamic.state = {
            name: newState,
            id: id
        };
        updateDataLayers();
        Navigation.onZoomChange();
        setStateClass()
    };

    const setStateClass = function () {
        const mainEl = document.querySelector(_config.static.domIds.main);

        if (mainEl) {
            // 1. Find all classes starting with 'state-'
            const stateClasses = Array.from(mainEl.classList)
                .filter(className => className.startsWith('state-'));

            // 2. Remove them (using spread operator to pass them as individual arguments)
            if (stateClasses.length > 0) {
                mainEl.classList.remove(...stateClasses);
            }

            // 3. Add the new state class
            mainEl.classList.add('state-' + _config.dynamic.state.name);
        }
    };

    const getCurrentZoomLevel = function () {
        return getMap().getZoom()
    };

    const setZoomToDom = function () {
        var zoomLevel = getCurrentZoomLevel();
        const mapElement = getMapElement();

        if (mapElement) {
            // 1. Remove any class starting with 'z-'
            const classesToRemove = Array.from(mapElement.classList)
                .filter(c => c.startsWith('z-'));

            if (classesToRemove.length > 0) {
                mapElement.classList.remove(...classesToRemove);
            }

            // 2. Add the new zoom class
            mapElement.classList.add("z-" + zoomLevel);
        }
    };

    const getFeature = function (id) {
        var _f;
        getMap().data.forEach(function (feature) {
            if (feature.getProperty("id") == id) {
                _f = feature
            }
        });
        return _f
    };

    const zoomToFeature = function (feature) {
        var bounds = new google.maps.LatLngBounds;
        processPoints(feature.getGeometry(), bounds.extend, bounds);
        getMap().fitBounds(bounds)
    };

    const zoomToCountry = function (id) {
        var country = Countries.getCountry(id);
        if (country.fixedZoom || Core.simpleDepictionNeeded()) {
            focusOn(country.lat, country.lng, parseInt(country.zoom, 10))
        } else {
            var feature = getFeature(id);
            zoomToFeature(feature)
        }
    };

    var processPoints = function (geometry, callback, thisArg) {
        if (geometry instanceof google.maps.LatLng) {
            callback.call(thisArg, geometry)
        } else if (geometry instanceof google.maps.Data.Point) {
            callback.call(thisArg, geometry.get())
        } else {
            geometry.getArray().forEach(function (g) {
                processPoints(g, callback, thisArg)
            })
        }
    };

    const focusOn = function (lat, lng, zoom) {
        setCenter(lat, lng);
        if (Core.isDefined(zoom)) {
            setZoom(zoom);
        }
    };

    const setCenter = function (lat, lng) {
        if (Core.isDefined(lat) && Core.isDefined(lng)) {
            getMap().panTo({ lat: Number(lat), lng: Number(lng) });
        }
    };

    const setZoom = function (zoom) {
        if (Core.isDefined(zoom)) {
            if (Core.isInMobileView()) {
                var w = $(_config.static.domIds.contentContainerMain).offsetWidth;

                zoom -= w < 360 && _config.dynamic.state.name == 'world' ? 2 : 1;
            }
            getMap().setZoom(parseInt(zoom));
        }
    };

    const update = function (o) {
        var type, id;

        if (o.navigation !== null) {
            type = o.navigation.type;
            id = o.navigation.id;
            if (type == 'country') {
                id = Continents.getContinentIdByCountry(o.navigation.id);
            } else if (type == 'world') {
                id = 'world';
            }
            Navigation.updateTopNav(id);
        }

        Villages.updateVillages();
        Projects.updateProjects();
        Cluster.updateCluster();

        if (o.focus != null) {
            focusOn(o.focus.lat, o.focus.lng, o.focus.zoom);
        }

        if (o.content !== null) {
            google.maps.event.addListenerOnce(SOSMap.getMap(), 'idle', function () {
                Content.update(o.content.type, o.content.id);
            });
        }
    };

    const mapDataLayerMouseover = function (event) {
        var feature = event.feature;
        var id = feature.getProperty('id');
        if (_config.dynamic.state.name == 'world') {
            if (Core.isTouchDevice()) {
                setCountryMarkerVisible(id);
            }
        } else {
            if (feature.getProperty('type') == 'country') {
                setCountryMarkerVisible(id);
            }
        }

        getMap().data.overrideStyle(feature, dataLayerStyleOverride(feature));
        Navigation.onDataLayerOver(feature);
    };

    const dataLayerStyleOverride = function (feature) {
        var configStyle;
        if (feature.getProperty('type') == 'continent') {
            configStyle = getMapStyle('mouseOver', 'continent');
        } else {
            configStyle = getMapStyle('mouseOver', 'country');
        }
        return configStyle;
    };

    var oldFeature;
    const mapDataLayerClick = function (event) {
        if (Core.isTouchDevice() && oldFeature != event.feature) {
            if (oldFeature) mapDataLayerMouseout({ feature: oldFeature });
            mapDataLayerMouseover(event);
            oldFeature = event.feature;
        } else {
            oldFeature = null;
            var feature = event.feature;
            var id = feature.getProperty('id');
            var type = feature.getProperty('type');
            Address.change(type, id);
        }
    };

    const addMapDataLayerEventListeners = function () {
        if (!Core.isTouchDevice()) {
            getMap().data.addListener("mouseover", function (e) {
                mapDataLayerMouseover(e)
            });
            getMap().data.addListener("mouseout", mapDataLayerMouseout)
        }
        getMap().data.addListener("click", function (e) {
            mapDataLayerClick(e)
        })
    };

    const init = () => {
        createMapCanvasDom();
        extendGoogleMaps();

        const mapEl = document.querySelector(_config.static.domIds.map.canvas);
        const styled = new google.maps.StyledMapType(
            Core.simpleDepictionNeeded()
                ? _config.static.mapStyleDetail
                : _config.static.mapStyle
        );

        // This call will now correctly use the Google Maps constructor
        const map = new google.maps.Map(mapEl, _config.static.mapOptions);
        map.mapTypes.set('map_style', styled);
        map.setMapTypeId('map_style');
        setMap(map);

        setMapDataLayerDefaultStyle();

        google.maps.event.addListenerOnce(map, 'idle', () => {
            Villages.init();
            Continents.init();
            Countries.init();
            Projects.init();
            Cluster.init();
            Navigation.init();
            Search.init();
            addMapDataLayerEventListeners();
            Address.init();
        });
    };

    return { init, getMap, stateChange, zoomToCountry, update };
})();

/* ============================================================
 * Search Module � Vanilla JS
 * ============================================================ */
const Search = new function () {

    var _keywords = [];
    var _index = [];
    var _resultsContainer = null;

    /**
     * Add to search index
     * @private
     */
    var _addIndex = function (type, id, kwd, country) {
        // Vanilla replacement for jQuery.inArray
        if (_keywords.indexOf(kwd) === -1) {
            _keywords.push(kwd);
            _index.push({
                label: kwd,
                value: {
                    type: type,
                    id: id,
                    kwd: kwd,
                    country: country
                }
            });
        }
    };

    /**
     * Create the dom for the search
     * @private
     */
    var _createSearchDom = function () {
        var mainContainer = document.querySelector(_config.static.domIds.main);
        if (!mainContainer) return;

        var searchForm = document.createElement('form');
        searchForm.action = "#";
        searchForm.method = "get";
        searchForm.addEventListener('submit', function (e) {
            e.preventDefault();
            return false;
        });

        var inputField = document.createElement('input');
        // Setting multiple attributes at once
        Object.assign(inputField, {
            type: "text",
            id: _config.static.domIds.search.replace(/#/, ''),
            placeholder: SOSMapConfig.labelSearch,
            autocomplete: "off",
            spellcheck: false
        });

        inputField.setAttribute('autocorrect', 'off');
        inputField.setAttribute('autocapitalize', 'off');
        inputField.classList.add('mapNavSearch');

        // Create results container for the custom autocomplete logic
        _resultsContainer = document.createElement('div');
        _resultsContainer.classList.add('mapNavSearchResults');
        _resultsContainer.style.display = 'none';

        searchForm.appendChild(inputField);
        mainContainer.appendChild(searchForm);
        mainContainer.appendChild(_resultsContainer);
    };

    /**
     * Custom Autocomplete logic to replace jQuery UI
     * @private
     */
    var _handleAutocomplete = function (inputEl) {
        inputEl.addEventListener('input', function () {
            var term = this.value.toLowerCase();
            _resultsContainer.innerHTML = '';

            if (term.length < 1) {
                _resultsContainer.style.display = 'none';
                return;
            }

            // Filter logic (Same as jQuery UI filter)
            var filtered = _index.filter(function (item) {
                return item.label.toLowerCase().indexOf(term) !== -1;
            });

            // Your specific sorting logic preserved exactly
            filtered.sort(function (a, b) {
                var key1 = a.label.toLowerCase();
                var key2 = b.label.toLowerCase();

                if (key1.indexOf(term) < key2.indexOf(term)) return -1;
                if (key1.indexOf(term) > key2.indexOf(term)) return 1;
                if (key1 < key2) return -1;
                if (key1 > key2) return 1;

                return 0;
            });

            // Slice to 50 as per your original response limit
            var results = filtered.slice(0, 50);

            if (results.length > 0) {
                _resultsContainer.style.display = 'block';
                results.forEach(function (item) {
                    var div = document.createElement('div');
                    div.classList.add('search-result-item');
                    div.textContent = item.label;
                    div.addEventListener('click', function () {
                        _selectItem(item, inputEl);
                    });
                    _resultsContainer.appendChild(div);
                });
            } else {
                _resultsContainer.style.display = 'none';
            }
        });

        // Close search results when clicking outside
        document.addEventListener('click', function (e) {
            if (e.target !== inputEl && e.target !== _resultsContainer) {
                _resultsContainer.style.display = 'none';
            }
        });
    };

    /**
     * Logic for when an item is selected
     * @private
     */
    var _selectItem = function (item, inputEl) {
        var id = item.value.id;
        var type = item.value.type;

        Address.change(type, id, true);

        inputEl.blur();
        inputEl.value = Core.isInMobileView() ? item.value.kwd : '';
        _resultsContainer.style.display = 'none';
    };

    /**
     * init function
     * @private
     */
    var _init = function () {
        _createSearchDom();

        var searchInput = document.querySelector(_config.static.domIds.search);
        if (!searchInput) return;

        // Focus Logic
        searchInput.addEventListener('focus', function (event) {
            var tgt = event.target;
            setTimeout(function () {
                tgt.value = '';
            }, 100);
        });

        // Keypress Logic
        searchInput.addEventListener('keypress', function (event) {
            var keycode = event.keyCode || event.which;
            if (keycode == '13') {
                event.preventDefault();
                event.stopPropagation();

                // Trigger selection of first item if Enter is pressed? 
                // (Optional addition to match jQuery UI behavior)
            }
        });

        // Initialize Custom Autocomplete
        _handleAutocomplete(searchInput);
    };

    return {
        init: _init,
        addIndex: _addIndex
    };
};



/* ============================================================
 * Continents
 * ============================================================ */
const Continents = (() => {

    const init = () => {

        var continentsGeoJson = {
            type: "FeatureCollection",
            features: []
        };

        const map = SOSMap.getMap();

        Object.entries(_config.static.apiStatic.continents || {}).forEach(([key, value]) => {

            let c = value;

            continent = {
                type: "Feature",
                properties: {
                    id: key,
                    type: "continent",
                    isVisible: false
                },
                geometry: c.geometry
            };
            Search.addIndex("continent", key, c.name);
            continentsGeoJson.features.push(continent)
        });
        map.data.addGeoJson(continentsGeoJson);
    };

    const getContinents = function (dynamic) {
        if (dynamic) {
            return _config.dynamic.apiData.continents;
        } else {
            return _config.static.apiStatic.continents;
        }
    };

    const getContinent = function (id, dynamic) {
        var continents = getContinents(dynamic);
        return continents[id];
    };

    const getContinentByCountry = function (id, dynamic) {
        var continents = getContinents(dynamic);
        var i, j;
        for (i in continents) {
            var countries = continents[i].countries;
            for (j = 0; j < countries.length; j++) {
                if (countries[j] == id) {
                    return continents[i];
                }
            }
        }

        return getContinent(id, dynamic);
    };

    const getContinentNameOfCountry = function (id) {
        var continent = getContinentByCountry(id);
        return Core.isDefined(continent.name) ? continent.name : '';
    };

    const getContinentIdByCountry = (id) => {
        var continents = getContinents();
        var i, j;
        for (i in continents) {
            var countries = continents[i].countries;
            for (j = 0; j < countries.length; j++) {
                if (countries[j] == id) {
                    return i
                }
            }
        }
        return undefined
    };

    const isContinent = function (id, dynamic) {
        var continents = getContinents(dynamic);
        return !!(Core.isDefined(continents[id]));
    };

    return { init, getContinentIdByCountry, getContinentNameOfCountry, getContinents, getContinent, isContinent };
})();

/* ============================================================
 * Countries
 * ============================================================ */
const Countries = (() => {

    const data = {};
    let currentCountryId = '';

    let countryGeoJson = {
        type: "FeatureCollection",
        features: []
    };

    const init = () => {
        const map = SOSMap.getMap();
        let country = null;

        var staticCountries = getCountries();
        var dynamicCountries = getCountries(true);

        for (i in staticCountries) {
            if (dynamicCountries[i] !== undefined) {
                if (Core.simpleDepictionNeeded())
                    staticCountries[i].geometry.coordinates = [];
                country = {
                    type: "Feature",
                    properties: {
                        id: i,
                        continent: Continents.getContinentIdByCountry(i),
                        type: "country",
                        isVisible: true
                    },
                    geometry: staticCountries[i].geometry
                };
                countryGeoJson.features.push(country);
                Search.addIndex("country", i, dynamicCountries[i].name)
            }
        }
        SOSMap.getMap().data.addGeoJson(countryGeoJson)
    };

    const setCurrent = function (countryId) {
        currentCountryId = countryId;
        setCurrentLabels()
    };

    const getCurrent = () => {
        return currentCountryId;
    }

    const setCurrentLabels = function () {
        const mainContainer = document.querySelector(_config.static.domIds.main);

        if (mainContainer) {
            // Remove 'active' from any currently active labels within the container
            mainContainer.querySelectorAll(".map-marker-label.active").forEach(el => {
                el.classList.remove("active");
            });

            // Add 'active' to the specific country label
            const targetLabel = mainContainer.querySelector(".map-marker-label.country-" + currentCountryId);
            if (targetLabel) {
                targetLabel.classList.add("active");
            }
        }
    };

    const getCountry = function (id, dynamic) {
        var country = getCountries(dynamic);
        return country[id]
    };

    const getCountries = function (dynamic) {
        if (dynamic) {
            return _config.dynamic.apiData.countries;
        } else {
            return _config.dynamic.apiStatic.countries;
        }
    };

    const getByContinent = cid =>
        Object.values(data).filter(c => c.continent === cid);

    return { init, getCountry, getByContinent, setCurrent, getCurrent, getCountries };
})();

/* ============================================================
 * Villages
 * ============================================================ */
const Villages = (() => {
    /**
     * get marker data from config
     * @returns {*}
     * @private
     */
    var _getVillageData = function () {
        return _config.dynamic.apiData.villages;
    };

    /**
     * Creates an array of countries with its villages
     * @param {object} villages
     * @returns {Object} Object of countries
     * @private
     */
    var _sortVillagesByCountry = function (villages) {
        var markersSorted = {};
        for (var i in villages) {
            var item = villages[i].country;
            if (typeof markersSorted[item] === 'undefined') {
                markersSorted[item] = {};
            }
            markersSorted[item][i] = villages[i];
        }
        return markersSorted;
    };

    /**
     * Marker mouseover events
     * @param marker
     * @param markerType
     * @private
     */
    var _villageOverEvent = function (marker) {
        marker?.label?.el?.classList?.add('over');
    };

    /**
     * Marker mouseout events
     * @param marker
     * @param markerType
     * @private
     */
    var _villageOutEvent = function (marker) {
        marker?.label?.el?.classList?.remove('over');
    };

    /**
     * Navigate to a village by village villageId
     * @param countryId
     * @param villageId
     * @param {boolean} [updateNavigation]
     * @private
     */
    var _gotoVillageByIdAndCountry = function (countryId, villageId, updateNavigation) {
        var marker = _config.villages[countryId][villageId];
        _gotoVillage(marker, updateNavigation);
    };

    /**
     * Go to a village marker
     * @param marker
     * @param updateNavigation
     * @private
     */
    var _gotoVillage = function (marker, updateNavigation) {
        var markerPosition = marker.getPosition();
        if (typeof updateNavigation === 'undefined' || updateNavigation === false) {
            Navigation.onDataLayerClick(marker.country, 'country', marker.id);
        }
        SOSMap.focusOn(markerPosition.lat(), markerPosition.lng(), _config.static.detailZoom);
        Navigation.updateTopNav(marker.continent);
    };

    /**
     * Marker click event
     * @param marker
     * @private
     */
    var oldVillage;
    var _villageClickEvent = function (marker) {
        var isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

        if (isTouch && oldVillage !== marker) {
            if (oldVillage) _villageOutEvent(oldVillage, 'village');
            _villageOverEvent(marker, 'village');
            oldVillage = marker;
        } else {
            oldVillage = null;
            if (isTouch) _villageOutEvent(marker, 'village');
            // Address.change('village', marker.id);
        }
    };

    /**
     * Add marker event listener
     * @param marker
     * @param markerType
     * @private
     */
    var _addVillage = function (marker, markerType) {
        marker.addListener('mouseover', function () {
            _villageOverEvent(marker, markerType);
        });
        marker.addListener('mouseout', function () {
            _villageOutEvent(marker, markerType);
        });
        marker.addListener('click', function () {
            _villageClickEvent(marker, markerType);
        });
    };

    /**
     * Change village visibility
     * @param status
     * @private
     */
    var _setVillageVisibility = function (status) {
        var villageMarkers = _config.villages;
        for (var i in villageMarkers) {
            for (var j in villageMarkers[i]) {
                // Assuming addToMap is a custom method on your marker object
                villageMarkers[i][j].addToMap(status === true ? SOSMap.getMap() : null);
            }
        }
    };

    /**
     * Only show villages of current country
     * @private
     */
    var _showCountryVillages = function () {
        var currentCountry = Countries.getCurrent();
        var villageMarkers = _config.villages;

        if (typeof currentCountry !== 'undefined' && currentCountry !== null) {
            for (var i in villageMarkers) {
                for (var j in villageMarkers[i]) {
                    villageMarkers[i][j].addToMap(i == currentCountry ? SOSMap.getMap() : null);
                }
            }
        } else {
            for (var i in villageMarkers) {
                for (var j in villageMarkers[i]) {
                    villageMarkers[i][j].addToMap(null);
                }
            }
        }
    };

    /**
     * Show or hide villages depending on map parameters
     * @private
     */
    var _updateVillages = function () {
        var state = _config.dynamic.state.name;
        if (state === 'world') {
            _hideVillages();
        } else if (state === 'country' || state === 'village' || state === 'project') {
            _showCountryVillages();
        } else {
            _hideVillages();
        }
    };

    /**
     * Hide all villages
     * @private
     */
    var _hideVillages = function () {
        _setVillageVisibility(false);
    };

    /**
     * Create marker
     * @param markerData
     * @param id
     * @returns {google.maps.Marker}
     * @private
     */
    var _createVillageMarker = function (markerData, id) {
        var t = 'continent';
        if (markerData.continent !== undefined && markerData.country !== undefined) {
            t = 'village';
        } else if (markerData.continent !== undefined && markerData.country === undefined) {
            t = 'country';
        }

        var marker = new google.maps.Marker({
            position: { lat: Number(markerData.geodata.lat), lng: Number(markerData.geodata.lng) },
            title: '',
            icon: { url: _config.static.marker.default.normal.icon },
            type: t,
            continent: markerData.continent,
            country: markerData.country,
            id: id,
            labelTitle: markerData.name,
            label: markerData.name.constructor,
            labelClass: 'map-marker-label map-marker-label-village',
            size: _config.static.marker.default.size,
            zIndex: 1
        });

        _addVillage(marker, 'village');
        return marker;
    };

    /**
     * @param id
     * @returns {Array|Object}
     * @private
     */
    var _getVillagesByCountry = function (id) {
        if (typeof _config.villages[id] !== 'undefined') {
            return _config.villages[id];
        }
        return [];
    };

    /**
     * Get single village by id
     * @param id
     * @returns {*}
     * @private
     */
    var _getVillage = function (id) {
        var villages = _getVillages();
        return villages[id];
    };

    /**
     * Get villages
     * @returns {Object}
     * @private
     */
    var _getVillages = function () {
        return _config.dynamic.apiData.villages;
    };

    /**
     * Convert given village list to marker list
     * @param villageList
     * @returns {Object}
     * @private
     */
    var _createVillageMarkers = function (villageList) {
        var markerList = {};
        for (var i in villageList) {
            var item = villageList[i];
            markerList[i] = {};

            for (var j in item) {
                markerList[i][j] = _createVillageMarker(item[j], j);
                if (typeof Search !== 'undefined') {
                    Search.addIndex('village', j, markerList[i][j].labelTitle, item[j].country);
                }
            }
        }
        return markerList;
    };

    /**
     * Set up villages
     * @private
     */
    var _loadVillages = function () {
        var villageList = _createVillageList();
        _config.villages = _createVillageMarkers(villageList);
    };

    /**
     * @param regionType
     * @param id
     * @returns {Number}
     * @private
     */
    var _getVillageCount = function (regionType, id) {
        var villages = _getVillageData();
        var count = 0;
        var type = regionType === 1 ? 'continent' : 'country';

        for (var i in villages) {
            if (id === villages[i][type]) {
                count++;
            }
        }
        return count;
    };

    /**
     * @returns {Object}
     * @private
     */
    var _createVillageList = function () {
        return _sortVillagesByCountry(_getVillageData());
    };

    /**
     * Init function
     * @private
     */
    var _init = function () {
        _loadVillages();
    };

    return {
        init: _init,
        getVillageCount: _getVillageCount,
        getVillagesByCountry: _getVillagesByCountry,
        updateVillages: _updateVillages,
        gotoVillageByIdAndCountry: _gotoVillageByIdAndCountry,
        getVillage: _getVillage
    };
})();

/* ============================================================
 * Projects
 * ============================================================ */
var Projects = new (function () {

    /**
     * Change project visibility
     * @param {boolean} status
     * @private
     */
    var _setProjectVisibility = function (status) {
        var projectMarkers = _config.projects;
        for (var i in projectMarkers) {
            for (var j in projectMarkers[i]) {
                // Assuming addToMap is a custom method on your marker object
                projectMarkers[i][j].addToMap(status === true ? SOSMap.getMap() : null);
            }
        }
    };

    /**
     * Navigate to a project by country & project id
     * @private
     */
    var _gotoProjectByIdAndCountry = function (countryId, projectId, contentLoad, navload) {
        var marker = _config.projects[countryId][projectId];
        _gotoProject(marker, contentLoad, navload);
    };

    /**
     * Create emergency ticker items
     * @private
     */
    var _createEmergency = function () {
        var projects = _config.projects;
        var list = document.createElement('ul');
        list.className = 'jq-news-ticker-items';

        var projectData = _config.dynamic.apiData.projects;
        var completed = [];

        for (var i in projects) {
            var country = projects[i];
            for (var j in country) {
                var project = projectData[j];

                // Skip if already processed or if no ticker content exists
                if (!completed.includes(j)) {
                    var hasTicker = project.tickercontent &&
                        ((project.tickercontent.headline && project.tickercontent.headline !== '') ||
                            (project.tickercontent.content && project.tickercontent.content !== ''));

                    if (hasTicker) {
                        var itemHtml = '+++ ';
                        if (project.tickercontent.url) {
                            itemHtml += '<a href="' + project.tickercontent.url + '">';
                        }
                        if (project.tickercontent.headline) {
                            itemHtml += '<span class="title">' + project.tickercontent.headline + '</span> &ndash; ';
                        }
                        if (project.tickercontent.content) {
                            itemHtml += project.tickercontent.content;
                        }
                        if (project.tickercontent.url) {
                            itemHtml += '</a>';
                        }
                        itemHtml += ' +++';

                        var li = document.createElement('li');
                        li.className = 'jq-news-ticker-item';
                        li.dataset.id = j;
                        li.innerHTML = itemHtml;
                        list.appendChild(li);
                    }
                }
                completed.push(j);
            }
        }

        var emergencyElm = document.querySelector(_config.static.domIds.emergency);
        if (emergencyElm) {
            emergencyElm.appendChild(list);
            // Note: .liScroll() is a jQuery plugin. 
            // You will need a vanilla alternative or CSS animation here.
            // if (window.VanillaTicker) new VanillaTicker(list); 
        }
    };

    /**
     * Ticker : show emergency project in country
     * @param {string} id - Country ID
     * @private
     */
    var _showEmergencyInCountry = function (id) {
        var emergencyContainer = document.querySelector(_config.static.domIds.emergency);
        var mainContainer = document.querySelector(_config.static.domIds.main);

        if (!emergencyContainer) return;

        var listItems = emergencyContainer.querySelectorAll('li');
        listItems.forEach(li => li.classList.remove('active'));

        if (id) {
            var projects = Projects.getProjectsByCountry(id);
            var projectStack = Object.keys(projects);

            if (mainContainer) mainContainer.classList.remove('emergency');

            listItems.forEach(li => {
                if (projectStack.includes(li.dataset.id)) {
                    li.classList.add('active');
                    if (mainContainer) mainContainer.classList.add('emergency');
                }
            });
        }
        _showEmergency();
    };

    /**
     * @private
     */
    var _hideEmergency = function () {
        var e = document.querySelector(_config.static.domIds.emergency);
        var m = document.querySelector(_config.static.domIds.main);
        if (e) e.style.visibility = 'hidden';
        if (m) m.classList.remove('emergency');
    };

    /**
     * @private
     */
    var _showEmergency = function () {
        var e = document.querySelector(_config.static.domIds.emergency);
        if (!e) return;

        var hasActive = e.querySelector('li.active') !== null;
        e.style.visibility = hasActive ? 'visible' : 'hidden';
    };

    /**
     * Only show projects of current country
     * @private
     */
    var _showCountryProjects = function () {
        var currentCountry = Countries.getCurrent();
        var map = SOSMap.getMap();
        var projectMarkers = _config.projects;

        for (var i in projectMarkers) {
            for (var j in projectMarkers[i]) {
                var marker = projectMarkers[i][j];
                marker.addToMap(marker.country === currentCountry ? map : null);
            }
        }
    };

    /**
     * Show or hide projects depending on map state
     * @private
     */
    var _updateProjects = function () {
        var state = _config.dynamic.state.name;
        if (state === 'world') {
            _hideProjects();
            _hideEmergency();
        } else if (['country', 'project', 'village'].includes(state)) {
            _showCountryProjects();
            _showEmergency();
        } else {
            _hideProjects();
            _hideEmergency();
        }
    };

    /**
     * @private
     */
    var _hideProjects = function () {
        _setProjectVisibility(false);
    };

    /**
     * @returns {*}
     * @private
     */
    var _getProjectData = function () {
        return _config.dynamic.apiData.projects;
    };

    /**
     * Creates an object of countries with its projects
     * @private
     */
    var _sortProjectsByCountry = function (projects) {
        var markersSorted = {};
        for (var i in projects) {
            var p = projects[i];
            if (p.countries && p.countries.length > 0) {
                p.countries.forEach(countryId => {
                    if (typeof markersSorted[countryId] === 'undefined') {
                        markersSorted[countryId] = {};
                    }
                    markersSorted[countryId][i] = p;
                });
            }
        }
        return markersSorted;
    };

    /**
     * Marker mouseover events
     * @private
     */
    var _projectOverEvent = function (marker) {
        var container = document.querySelector(_config.static.domIds.subNav.projects);
        if (container) {
            var el = container.querySelector('.projects-' + marker.id);
            if (el) el.classList.add('over');
        }
        if (marker.label && marker.label.span) {
            marker.label.span.classList.add('over');
        }
    };

    /**
     * Marker mouseout events
     * @private
     */
    var _projectOutEvent = function (marker) {
        var container = document.querySelector(_config.static.domIds.subNav.projects);
        if (container) {
            container.querySelectorAll('.over').forEach(el => el.classList.remove('over'));
        }
        if (marker.label && marker.label.span) {
            marker.label.span.classList.remove('over');
        }
    };

    /**
     * Go to a project marker
     * @private
     */
    var _gotoProject = function (marker, contentLoad, navload) {
        var markerPosition = marker.getPosition();

        if (typeof navload === 'undefined' || navload === false) {
            Navigation.onDataLayerClick(marker.country, 'country', marker.id);
        }

        SOSMap.focusOn(markerPosition.lat(), markerPosition.lng(), _config.static.detailZoom);
        Navigation.updateTopNav(marker.continent);
    };

    /**
     * Marker click event
     * @private
     */
    var oldProject;
    var _projectClickEvent = function (marker) {
        var isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

        if (isTouch && oldProject !== marker) {
            if (oldProject) _projectOutEvent(oldProject);
            _projectOverEvent(marker);
            oldProject = marker;
        } else {
            oldProject = null;
            if (isTouch) _projectOutEvent(marker);
            // Address.change('project', marker.id);
        }
    };

    /**
     * @private
     */
    var _addProject = function (marker, markerType) {
        marker.addListener('mouseover', () => _projectOverEvent(marker, markerType));
        marker.addListener('mouseout', () => _projectOutEvent(marker, markerType));
        marker.addListener('click', () => _projectClickEvent(marker, markerType));
    };

    /**
     * @private
     */
    var _createProjectMarker = function (country, markerData, id) {
        var geodata = markerData.geodata;

        // Fallback to country center if geodata is missing or multi-country project
        if (!geodata || !geodata.lat || !geodata.lng || markerData.countries.length > 1) {
            var feature = SOSMap.getFeature(country);
            var countryCenter = SOSMap.getFeatureCenter(feature);
            geodata = { lat: countryCenter.lat(), lng: countryCenter.lng() };
        }

        var marker = new google.maps.Marker({
            position: { lat: Number(geodata.lat), lng: Number(geodata.lng) },
            title: '',
            icon: { url: _config.static.marker.default.normal.icon },
            type: 'project',
            country: country,
            id: id,
            labelTitle: markerData.name,
            label: markerData.name.constructor,
            labelClass: 'map-marker-label map-marker-label-project',
            size: _config.static.marker.default.size,
            zIndex: 2
        });

        _addProject(marker, 'project');
        return marker;
    };

    /**
     * @private
     */
    var _createProjectMarkers = function (projectList) {
        var markerList = {};
        for (var i in projectList) {
            var item = projectList[i];
            markerList[i] = {};
            for (var j in item) {
                markerList[i][j] = _createProjectMarker(i, item[j], j);
                if (typeof Search !== 'undefined') {
                    Search.addIndex('project', j, markerList[i][j].labelTitle, i);
                }
            }
        }
        return markerList;
    };

    /**
     * @private
     */
    var _getProjectsByCountry = function (id) {
        return _config.projects[id] || {};
    };

    /**
     * @private
     */
    var _getProjectCount = function (regionType, id) {
        var projects = _config.projects;
        var count = 0;

        if (regionType === 1) { // continent
            for (var i in projects) {
                var pCountries = projects[i];
                for (var j in pCountries) {
                    if (id === Continents.getContinentIdByCountry(pCountries[j].country)) {
                        count++;
                    }
                }
            }
            return count;
        } else if (regionType === 2) { // country
            var p = projects[id];
            return (p && typeof p === 'object') ? Object.keys(p).length : 0;
        }
        return 0;
    };

    /**
     * @private
     */
    var _getProject = function (id) {
        return _getProjects()[id];
    };

    /**
     * @private
     */
    var _getProjects = function () {
        return _config.dynamic.apiData.projects;
    };

    /**
     * @private
     */
    var _createProjectList = function () {
        return _sortProjectsByCountry(_getProjectData());
    };

    /**
     * @private
     */
    var _loadProjects = function () {
        _config.projects = _createProjectMarkers(_createProjectList());
    };

    /**
     * @private
     */
    var _createEmergencyDom = function () {
        //var item = document.createElement('div');
        //item.id = _config.static.domIds.emergency.replace(/#/, '');
        //item.className = 'mapNavEmergency';
        //var mainMap = document.querySelector(_config.static.domIds.map.main);
        //if (mainMap) mainMap.appendChild(item);
    };

    /**
     * @private
     */
    var _init = function () {
        _loadProjects();
        _createEmergencyDom();
        _createEmergency();
    };

    return {
        init: _init,
        updateProjects: _updateProjects,
        getProjectsByCountry: _getProjectsByCountry,
        gotoProjectByIdAndCountry: _gotoProjectByIdAndCountry,
        getProjectCount: _getProjectCount,
        getProject: _getProject,
        showEmergencyInCountry: _showEmergencyInCountry
    };
})();

/* ============================================================
 * Cluster
 * ============================================================ */
const Cluster = new function () {

    // Ensure config object exists
    _config.cluster = {
        continent: {},
        country: {}
    };

    /**
     * Change cluster visibility
     * @param type
     * @param status
     * @private
     */
    var _setClusterVisibility = function (type, status) {
        var map = SOSMap.getMap();
        var clusters = _config.cluster[type];
        for (var i in clusters) {
            // Assuming .addToMap is a custom method on your marker objects
            clusters[i].addToMap(status === true ? map : null);
        }
    };

    /**
     * Show or hide clusters
     * @private
     */
    var _updateCluster = function () {
        var n = _config.dynamic.state.name;
        var style = 'map_style';

        if (n === 'world') {
            _hideCluster('country');
            _showCluster('continent');
        } else if (n === 'continent') {
            _hideCluster('continent');
            _hideCluster('country');
        } else {
            _hideCluster('continent');
            _hideCluster('country');
            if (n === 'project' || n === 'village') {
                style = 'map_style_detail';
            }
        }

        // Updated Map to SOSMap
        SOSMap.getMap().setMapTypeId(style);
    };

    /**
     * Show all projects
     * @private
     */
    var _showCluster = function (type) {
        _setClusterVisibility(type, true);
    };

    /**
     * Hide all projects
     * @private
     */
    var _hideCluster = function (type) {
        _setClusterVisibility(type, false);
    };

    /**
     * Calculate marker size
     * @param cnt
     * @returns {number}
     * @private
     */
    var _getMarkerSize = function (cnt) {
        if (cnt >= 100) {
            return 100;
        } else if (cnt >= 10) {
            return 10;
        } else {
            return 1;
        }
    };

    /**
     * Marker mouseover events
     * @param marker
     * @private
     */
    var _markerOverEvent = function (marker) {
        // Vanilla JS classList for cleaner manipulation
        if (marker.label && marker.label.span) {
            marker.label.span.classList.add('over');
        }
        // Updated Map to SOSMap
        SOSMap.setMouseover(marker.action.id);
    };

    /**
     * Marker mouseout events
     * @param marker
     * @private
     */
    var _markerOutEvent = function (marker) {
        if (marker.label && marker.label.span) {
            marker.label.span.classList.remove('over');
        }
        // Updated Map to SOSMap
        SOSMap.setMouseout(marker.action.id);
    };

    /**
     * Marker click events
     * @param marker
     * @private
     */
    var oldCluster;
    var _markerClickEvent = function (marker) {
        if (Core.isTouchDevice() && oldCluster !== marker) {
            if (oldCluster) {
                _markerOutEvent(oldCluster);
            }
            _markerOverEvent(marker);
            oldCluster = marker;
        } else {
            oldCluster = null;
            if (Core.isTouchDevice()) {
                _markerOutEvent(marker);
            }
            Address.change(marker.action.type, marker.action.id);
        }
    };

    /**
     * Add marker event listener
     * @param marker
     * @private
     */
    var _addMarkerEvents = function (marker) {
        // Using Google Maps API addListener (Vanilla)
        marker.addListener('mouseover', function () {
            _markerOverEvent(marker);
        });
        marker.addListener('mouseout', function () {
            _markerOutEvent(marker);
        });
        marker.addListener('click', function () {
            _markerClickEvent(marker);
        });
    };

    /**
     * Create the country clusters
     * @param id
     * @param country
     * @private
     */
    var _createCountryCluster = function (id, country) {
        var villageCnt = Villages.getVillageCount(2, id);
        var projectCnt = Projects.getProjectCount(2, id);

        var type = (villageCnt === 0) ? 'novillage' : 'normal';
        var title = Countries.getCountry(id, true).name;
        var size = _getMarkerSize(villageCnt);

        if (country) {
            var marker = new google.maps.Marker({
                position: { lat: Number(country.lat), lng: Number(country.lng) },
                title: '',
                icon: { url: _config.static.marker.default.normal.icon },
                clickable: false,
                type: type,
                markerPositions: true,
                cnt: size,
                action: { type: 'country', id: id },
                label: null,
                country: id,
                labelTitle: title,
                labelVillages: villageCnt > 0 ? String(villageCnt) : '',
                labelProjects: (projectCnt > 0) ? String(projectCnt) : undefined,
                //labelClass: 'map-marker-label map-marker-label-cluster-country size' + size + ' ' + type,
                labelClass: 'map-marker-label map-marker-label-cluster-country',
                size: _config.static.cluster[type][size].size,
                zIndex: 1
            });

            _config.cluster.country[id] = marker;
            _addMarkerEvents(marker);
        }
    };

    /**
     * Create the continent clusters
     * @param id
     * @param continent
     * @private
     */
    var _createContinentCluster = function (id, continent) {
        var villageCnt = Villages.getVillageCount(1, id);
        var projectCnt = Projects.getProjectCount(1, id);
        var type = 'normal';
        var title = Continents.getContinent(id, true).name;
        var size = _getMarkerSize(villageCnt);

        var marker = new google.maps.Marker({
            position: { lat: Number(continent.lat), lng: Number(continent.lng) },
            title: '',
            icon: { url: _config.static.marker.default.normal.icon },
            type: type,
            markerPositions: true,
            cnt: size,
            action: { type: 'continent', id: id },
            label: null,
            continent: id,
            labelTitle: title,
            labelVillages: String(villageCnt),
            labelProjects: (projectCnt > 0) ? String(projectCnt) : undefined,
            //labelClass: 'map-marker-label map-marker-label-cluster-continent size' + size,
            labelClass: 'map-marker-label map-marker-label-cluster-continent',
            size: _config.static.cluster[type][size].size,
            zIndex: 1
        });

        _config.cluster.continent[id] = marker;
        _addMarkerEvents(marker);
    };

    /**
     * Set up continent clusters
     * @private
     */
    var _drawContinentClusters = function () {
        var continents = Continents.getContinents();
        for (var i in continents) {
            _createContinentCluster(i, continents[i]);
        }
    };

    /**
     * Set up country clusters
     * @private
     */
    var _drawCountryClusters = function () {
        var countries = Countries.getCountries(true);
        for (var i in countries) {
            _createCountryCluster(i, Countries.getCountry(i));
        }
    };

    /**
     * Init
     * @private
     */
    var _init = function () {
        _drawContinentClusters();
        _drawCountryClusters();
        _updateCluster();
    };

    return {
        init: _init,
        updateCluster: _updateCluster
    };
};
/* ============================================================
 * Navigation
 * ============================================================ */
const Navigation = (() => {

    /**
     * Called on navigation village click
     * @private
     */
    var _onSelectVillage = function () {
        // Find the selected option within the select element
        var sel = this.options[this.selectedIndex];
        // Check if class contains 'project'
        var type = sel.className.indexOf('project') !== -1 ? 'project' : 'village';

        // Vanilla replacement for jQuery.scrollTo with -50 offset
        var targetEl = document.querySelector(_config.static.domIds.main);
        if (targetEl) {
            var topOffset = targetEl.getBoundingClientRect().top + window.pageYOffset - 50;
            window.scrollTo({
                top: topOffset,
                behavior: 'smooth'
            });
        }

        Address.change(type, sel.getAttribute('data-id') || sel.dataset.id);
    };

    /**
     * Add villages
     * @param id
     * @param highlightId
     * @private
     */
    var _addVillages = function (id, highlightId) {
        var villages = Villages.getVillagesByCountry(id);
        var villagesSorted = [];

        for (var i in villages) {
            var item = villages[i];
            item.id = i;
            villagesSorted.push(item);
        }

        villagesSorted = villagesSorted.sort(function (a, b) {
            if (a.labelTitle < b.labelTitle) return -1;
            if (a.labelTitle > b.labelTitle) return 1;
            return 0;
        });

        addVillagesToDropDown(villagesSorted, highlightId);
    };

    var addVillagesToDropDown = function (villagesSorted, highlightId) {
        var i, item, dd = document.querySelector(_config.static.domIds.villageSelect);
        if (!dd) return;

        // Clear children and remove class
        dd.innerHTML = '';
        dd.classList.remove('has-options');

        if (villagesSorted.length > 0) {
            for (i = 0; i < villagesSorted.length; i++) {
                var sortedVillage = villagesSorted[i];
                sortedVillage.labelNumber = i + 1;

                item = document.createElement('option');
                item.textContent = (i + 1) + '. ' + sortedVillage.labelTitle;

                // Set data attributes
                item.dataset.id = sortedVillage.id;
                item.dataset.country = sortedVillage.country;

                item.classList.add("villages-" + sortedVillage.id);

                if (Core.isDefined(highlightId) && (sortedVillage.id == highlightId)) {
                    item.classList.add("active");
                }

                dd.appendChild(item);
            }
        }
    };

    /**
     * Add projects
     * @param id
     * @param highlightId
     * @private
     */
    var _addProjects = function (id, highlightId) {
        var projects = Projects.getProjectsByCountry(id);
        var item;
        var projectsSorted = [];

        for (var i in projects) {
            item = projects[i];
            item.id = i;
            projectsSorted.push(item);
        }

        projectsSorted = projectsSorted.sort(function (a, b) {
            if (a.labelTitle < b.labelTitle) return -1;
            if (a.labelTitle > b.labelTitle) return 1;
            return 0;
        });

        _addProjectsToDropDown(projectsSorted, highlightId);
    };

    var _addProjectsToDropDown = function (projectsSorted, highlightId) {
        var i, item, dd = document.querySelector(_config.static.domIds.villageSelect);
        if (!dd) return;

        var vCount = dd.querySelectorAll('option').length;
        var cnt = vCount;

        if (projectsSorted.length > 0) {
            var o = document.createElement('option');
            o.disabled = true;
            o.textContent = '-- ' + SOSMapConfig.labelProjects + ' --';
            dd.appendChild(o);

            for (i = 0; i < projectsSorted.length; i++) {
                var sortedP = projectsSorted[i];
                cnt++;
                sortedP.labelNumber = cnt;

                item = document.createElement('option');
                item.textContent = cnt + '. ' + sortedP.labelTitle;
                item.dataset.id = sortedP.id;
                item.classList.add("projects-" + sortedP.id);

                if (Core.isDefined(highlightId) && (sortedP.id == highlightId)) {
                    item.classList.add("active");
                }

                dd.appendChild(item);
            }
        }

        if (vCount > 0) {
            _addDropdownTitle(dd, vCount, projectsSorted.length);
            dd.classList.add('has-options');
        } else {
            dd.classList.remove('has-options');
        }
    };

    var _addDropdownTitle = function (dd, vCount, pCount) {
        var o = document.createElement('option');
        o.selected = true;
        o.textContent = SOSMapConfig.labelVillageList;
        dd.prepend(o);
        // Ensure the first option is selected
        dd.options[0].selected = true;
    };

    /**
     * Update back to..
     * @param id
     * @private
     */
    var _updateParentReturn = function (id) {
        var name = Continents.getContinentNameOfCountry(id);
        var parentEl = document.querySelector(_config.static.domIds.subNav.parent);
        if (parentEl) {
            var countryEl = parentEl.querySelector('.country');
            if (countryEl) {
                countryEl.dataset.id = id;
                countryEl.textContent = name;
            }
        }
    };

    /**
     * Show subnavigation
     */
    var _showSubNavigation = function (id, type, highlightId) {
        var subNavMain = document.querySelector(_config.static.domIds.subNav.main);

        if (_config.dynamic.state.name === 'country' || _config.dynamic.state.name === 'village' || _config.dynamic.state.name === 'project') {
            _updateParentReturn(id);
            _addVillages(id, highlightId);
            _addProjects(id, highlightId);

            if (subNavMain) {
                subNavMain.classList.add('country');
                subNavMain.classList.remove('continent', 'village');
            }

        } else if (_config.dynamic.state.name === 'continent') {
            if (!Continents.isContinent(id)) {
                id = Continents.getContinentIdByCountry(id);
            }
            if (subNavMain) {
                subNavMain.classList.add('continent');
                subNavMain.classList.remove('country', 'village');
            }
        }

        if (_config.dynamic.state.name === 'country') {
            Countries.setCurrent(id);
        }
    };

    /**
     * Called on data layer mouseover
     * @param feature
     * @private
     */
    var _onDataLayerOver = function (feature) {
        var type = feature.getProperty('type');
        var id = feature.getProperty('id');

        if (type == 'country' || type == 'continent') {
            var subNavEl = document.querySelector(_config.static.domIds.subNav.main);
            if (subNavEl) {
                var target = subNavEl.querySelector('.' + type + '-' + id);
                if (target) target.classList.add('over');
            }

            var canvasEl = document.querySelector(_config.static.domIds.map.canvas);
            if (canvasEl) {
                var marker = canvasEl.querySelector('.map-marker-label-cluster-' + type + '.' + type + '-' + id);
                if (marker) marker.classList.add('over');
            }
        }
    };

    /**
     * Called on data layer mouseout
     * @private
     */
    var _onDataLayerOut = function () {
        var overElements = document.querySelectorAll('.over');
        overElements.forEach(function (el) {
            el.classList.remove('over');
        });
    };

    /**
     * Called on data layer click
     */
    var _onDataLayerClick = function (id, type, highlightId) {
        _onDataLayerOut();
        _showSubNavigation(id, type, highlightId);
    };

    /**
     * Called on map zoom level change
     * @private
     */
    var _onZoomChange = function () {
        var m = document.querySelector(_config.static.domIds.map.main);
        if (!m) return;

        var changed = false;
        if (_config.dynamic.state.name == 'world') {
            changed = m.classList.contains('navShown');
            m.classList.remove('navShown');
            m.classList.add('navHide');
        } else {
            changed = !m.classList.contains('navShown');
            m.classList.remove('navHide');
            m.classList.add('navShown');
        }

        if (changed) google.maps.event.trigger(SOSMap.getMap(), "resize");
    };

    /**
     * Update counter
     */
    var _updateTopNavCounter = function () {
        var continents = Continents.getContinents(true);
        var id;
        var topnav = document.querySelector(_config.static.domIds.topNav);
        if (!topnav) return;

        for (id in continents) {
            var cnt = Villages.getVillageCount(1, id);

            var worldWrap = topnav.querySelector("[data-id=world]");
            if (worldWrap) {
                var worldCnt = worldWrap.querySelector('.cnt');
                if (worldCnt) {
                    worldCnt.textContent = parseInt(worldCnt.innerHTML || 0) + cnt;
                }
            }

            var continentWrap = topnav.querySelector("[data-id=" + id + "]");
            if (continentWrap) {
                var contCnt = continentWrap.querySelector('.cnt');
                if (contCnt) contCnt.textContent = cnt;
            }
        }
    };

    /**
     * on back button continent click
     * @private
     */
    var _onParentContinentClick = function () {
        var id = this.dataset.id;
        var continentId = Continents.getContinentIdByCountry(id);
        Address.change('continent', continentId);
    };

    /**
     * On back button world click
     * @private
     */
    var _onParentWorldClick = function () {
        Address.change('world', 'world');
    };

    /**
     * Add event listeners
     * @private
     */
    var _addEvents = function () {
        var n = document.querySelector(_config.static.domIds.subNav.parent);
        if (n) {
            var continentBtn = n.querySelector('.continent');
            if (continentBtn) continentBtn.addEventListener('click', _onParentWorldClick);

            var countryBtn = n.querySelector('.country');
            if (countryBtn) countryBtn.addEventListener('click', _onParentContinentClick);
        }
    };

    /**
     * @private
     */
    var _updateTopNav = function (id) {
        if (Core.isDefined(id)) {
            var topnav = document.querySelector(_config.static.domIds.topNav);
            if (topnav) {
                topnav.querySelectorAll('li').forEach(function (li) {
                    li.classList.remove('active');
                });
                var activeLi = topnav.querySelector('li[data-id="' + id + '"]');
                if (activeLi) activeLi.classList.add('active');
            }
        }
    };

    /**
     * Create the top navigation dom
     * @private
     */
    var _createTopnavigation = function () {
        var i, continents;
        continents = Continents.getContinents(true);

        var topnav = document.createElement('ul');
        topnav.id = _config.static.domIds.topNav.replace(/#/, '');
        topnav.classList.add('mapNavTopNav');

        // Create World LI
        var worldLi = document.createElement('li');
        worldLi.dataset.id = "world";
        worldLi.textContent = SOSMapConfig.labelWorld;
        topnav.appendChild(worldLi);

        for (i in continents) {
            var contLi = document.createElement('li');
            contLi.dataset.id = i;
            contLi.textContent = continents[i].name;
            topnav.appendChild(contLi);
        }

        var mainContainer = document.querySelector(_config.static.domIds.main);
        if (mainContainer) mainContainer.appendChild(topnav);
    };

    /**
     * called on top navigation click event
     * @private
     */
    var _onTopNavigationClick = function () {
        var id = this.dataset.id;
        if (id != 'world') {
            Address.change('continent', id);
        } else {
            Address.change('world', id);
        }
    };

    /**
     * Add event listeners for top navigation
     * @private
     */
    var _addTopNavigationEvents = function () {
        var topnav = document.querySelector(_config.static.domIds.topNav);
        if (topnav) {
            var items = topnav.querySelectorAll('li');
            items.forEach(function (li) {
                li.addEventListener('click', _onTopNavigationClick);
            });
        }
    };

    /**
     * init the continent navigation
     * @private
     */
    var _initTopnavigation = function () {
        _createTopnavigation();
        _addTopNavigationEvents();
    };

    /**
     * Create side Navigation Dom
     * @private
     */
    var _createNavigationalDom = function () {
        var child;
        child = document.createElement('select');
        child.id = _config.static.domIds.villageSelect.replace(/#/, '');
        child.classList.add('mapNavVillageSelect');

        var mainContainer = document.querySelector(_config.static.domIds.main);
        if (mainContainer) mainContainer.appendChild(child);

        child.addEventListener('change', _onSelectVillage);
    };

    /**
     * Init function
     * @private
     */
    var _init = function () {
        _createNavigationalDom();
        _initTopnavigation();
        _updateTopNavCounter();
        _addEvents();
    };

    return {
        init: _init,
        onZoomChange: _onZoomChange,
        onDataLayerOver: _onDataLayerOver,
        onDataLayerOut: _onDataLayerOut,
        onDataLayerClick: _onDataLayerClick,
        updateTopNav: _updateTopNav
    };
})();

/* ============================================================
 * Address
 * ============================================================ */
const Address = (() => {

    const _init = function () {
        _bindEvent();
        _urlInit(window.location.href);
    };

    const _changeMap = function (state) {
        if (!state) return;

        let navtype, item, navid, lat, lng, geo, id;
        let type = state.type;
        id = state.id;
        navtype = type;

        // Logic branching for entity types
        if (type === 'continents') {
            type = 'continent';
            item = Continents.getContinent(id);
        } else if (type === 'countries') {
            type = 'country';
            item = Countries.getCountry(id);
        } else if (type === 'villages') {
            type = 'village';
            item = Villages.getVillage(id);
            navtype = 'continent';
            navid = item.continent;
        } else if (type === 'projects') {
            type = 'project';
            item = Projects.getProject(id);
            navtype = null;
        } else if (type === 'world') {
            SOSMap.stateChange('world', null);
            SOSMap.reset();
            Navigation.updateTopNav('world');
            SOSMap.update({ focus: null, content: null, navigation: { type: 'world', id: 'world' } });
            google.maps.event.addListenerOnce(SOSMap.getMap(), 'idle', () => {
                Content.update('world');
            });
            return;
        }

        // Processing Village/Project Logic
        if (type === 'village' || type === 'project') {
            const countryId = type === 'village' ? item.country : (item.countries[0] || null);
            Countries.setCurrent(countryId);
            SOSMap.stateChange(type, id);
            Navigation.onDataLayerClick(countryId, 'country', item.id);
            Navigation.updateTopNav(Continents.getContinentIdByCountry(countryId));

            lat = item.lat;
            lng = item.lng;

            if (item.geodata && item.geodata.lat && item.geodata.lng) {
                lat = item.geodata.lat;
                lng = item.geodata.lng;
            }

            // Handle missing coords for projects
            if (type === 'project' && (!lat || !lng)) {
                const feature = SOSMap.getFeature(countryId);
                const countryData = SOSMap.getFeatureCenter(feature);
                lat = countryData.lat();
                lng = countryData.lng();
            }

            let zoom = countryId === SOSMapConfig.localCountryId ? _config.static.localCountryDetailZoom : _config.static.detailZoom;
            const curCountry = Countries.getCountries()[countryId];
            if (curCountry && curCountry.zoom && parseInt(curCountry.zoom) > zoom) {
                zoom = parseInt(curCountry.zoom) + 1;
            }

            geo = { lat, lng, zoom };

            // Vanilla JS DOM replacement for jQuery find/removeClass/addClass
            const mainContainer = document.querySelector(_config.static.domIds.main);
            if (mainContainer) {
                mainContainer.querySelectorAll('.activeOver').forEach(el => el.classList.remove('activeOver'));
                const targetSelector = type === 'village' ? `.village-${item.id}` : `.project-${item.id}.country-${countryId}`;
                const targetEl = mainContainer.querySelector(targetSelector);
                if (targetEl) targetEl.classList.add('activeOver');
            }

        } else if (type === 'country') {
            Countries.setCurrent(id);
            SOSMap.stateChange(type, id);
            SOSMap.zoomToCountry(id);
            Navigation.onDataLayerClick(id, type);
            Navigation.updateTopNav(Continents.getContinentIdByCountry(id));
        } else if (type === 'continent') {
            SOSMap.stateChange(type, id);
            lat = item.lat;
            lng = item.lng;
            if (item.geodata) {
                lat = item.geodata.lat;
                lng = item.geodata.lng;
            }
            Navigation.updateTopNav(id);
            geo = { lat, lng, zoom: item.zoom ? parseInt(item.zoom) : 2 };
            Navigation.onDataLayerClick(id, type);
        }

        SOSMap.update({
            focus: geo,
            content: { type, id },
            navigation: navtype ? { type: navtype, id: navid } : null
        });
    };

    const _urlInit = function (url) {
        const regex = new RegExp(_config.static.baseUrl);
        url = url.replace(regex, '');
        _stateMatching(url);
    };

    const _stateMatching = function (hash) {
        let cleanHash = hash.replace(/\/$/, ""); // Remove trailing slash

        if (cleanHash.startsWith('/cms/getdoc')) {
            cleanHash = cleanHash.split('.aspx?')[0];
            cleanHash = cleanHash.substring(cleanHash.lastIndexOf('/'));
        }

        const urlMatch = _translateUrl(cleanHash);
        _changeMap(urlMatch);
    };

    const _onStateChange = function () {
        // Native History API uses window.location or event.state
        _stateMatching(window.location.pathname + window.location.search);
    };

    const _bindEvent = function () {
        // Native Popstate event instead of History.js bind
        window.addEventListener('popstate', _onStateChange);

        if (Core.isTouchDevice()) {
            window.addEventListener("orientationchange", () => {
                setTimeout(_onStateChange, 500);
            });
        }
    };

    const _matchUrl = function (dataBlock, url) {
        const entries = Object.entries(dataBlock);
        const match = entries.find(([key, val]) => val.url.toLowerCase().endsWith(url.toLowerCase()));

        if (match) {
            const result = { ...match[1] };
            result.id = match[0];
            return result;
        }
        return false;
    };

    const _translateUrl = function (url) {
        if (SOSMapConfig.hideVillages === 'True' && url.includes('?v=')) {
            const parts = url.split('&');
            if (parts.length > 1) url = parts[0];
        } else {
            url = url.split('?')[0];
        }

        const match = { id: 'world', type: 'world', url: _config.static.worldBaseUrl };

        if (url !== '/' && url !== '') {
            for (let category in _config.dynamic.apiData.raw) {
                const m = _matchUrl(_config.dynamic.apiData.raw[category], url);
                if (m) {
                    m.type = category;
                    return m;
                }
            }
        }
        return match;
    };

    const _change = function (name, id, fromSearch) {
        const isStartpage = _config.dynamic.state.name === 'world' && window.location.pathname === '/';

        _config.dynamic.state = { name, id, search: !!fromSearch };

        let data;
        switch (name) {
            case 'village': data = _config.dynamic.apiData.villages; break;
            case 'project': data = _config.dynamic.apiData.projects; break;
            case 'country': data = _config.dynamic.apiData.countries; break;
            case 'continent': data = _config.dynamic.apiData.continents; break;
        }

        let url = _config.static.worldBaseUrl;
        let item = null;

        if (name !== 'world') {
            item = data[id];
            if (!item || !item.url) return;
            url = item.url;
        }

        const title = `${SOSMapConfig.labelTitlePrefix} - ${item && item.name ? item.name : SOSMapConfig.labelWhereWeHelp}`;
        const repl = window.location.host.includes("viazenetti.de") ? `${window.location.protocol}//${window.location.host}` : '';
        url = url.replace(/http(s)?:\/\/[^\/]*/, repl);

        if (isStartpage) {
            window.location.href = url;
            return;
        }

        // Native pushState
        window.history.pushState(_config.dynamic.state, title, url.toLowerCase());
        // Manually trigger the logic because pushState doesn't fire popstate
        _stateMatching(url);
    };

    return {
        init: _init,
        change: _change,
        urlInit: _urlInit,
        // Setter for config if not global
        setConfig: (cfg) => { _config = cfg; }
    };
})();


/* ============================================================
 * Content
 * ============================================================ */
const Content = (() => {
    var _contentRequest;
    var _firstLoad = true;
    var actURL;

    /**
     * Set tracking (Adobe Analytics / SiteCatalyst parsing)
     * @param {string} data
     * @private
     */
    var _track = function (data) {
        var i, j, val;

        // Extract script parameters between specific markers
        var scriptSplit = data.split('<script language="JavaScript" type="text/javascript">')[1];
        if (typeof scriptSplit === 'undefined') return;

        var currentParamsStr = scriptSplit.split('var s_code=s.t()')[0];
        if (!currentParamsStr) return;

        var currentParams = currentParamsStr.replace(/(?:\r\n|\r|\n)/g, '').split(';');

        var parsedParams = [];
        for (i = 0; i < currentParams.length; i++) {
            var pair = currentParams[i].split('=');
            if (pair.length === 2) {
                parsedParams.push([pair[0].trim(), pair[1].trim()]);
            }
        }

        // Clean "s." prefix
        for (i = 0; i < parsedParams.length; i++) {
            if (parsedParams[i][0].indexOf('s.') === 0) {
                parsedParams[i][0] = parsedParams[i][0].split('s.')[1];
            }
        }

        try {
            if (window.s) {
                s.clearVars();
                for (i = 0; i < parsedParams.length; i++) {
                    var key = parsedParams[i][0];
                    val = parsedParams[i][1];

                    if (val.indexOf('"') > -1) {
                        val = val.replace(/"/g, '');
                    } else {
                        // Original code used eval to handle variable references
                        try { val = eval(val); } catch (e) { }
                    }
                    s[key] = val;
                }
                s.t();
            }
        } catch (e) {
            console.error("Tracking error:", e);
        }
    };

    /**
     * Replace the content in the page with a fade transition
     * @param {object} newContent
     * @private
     */
    var _replaceContent = function (newContent) {
        var mainEl = document.querySelector(_config.static.domIds.contentContainerMain);
        if (!mainEl) return;

        var contentEl = mainEl.querySelector(_config.static.domIds.contentContainerContent);
        var breadcrumbEl = mainEl.querySelector(_config.static.domIds.contentContainerBreadcrumb);
        var titleEl = mainEl.querySelector(_config.static.domIds.contentContainerTitle);

        if (contentEl) {
            // Replicate .animate({opacity: 0})
            contentEl.style.transition = 'opacity 200ms';
            contentEl.style.opacity = 0;

            setTimeout(function () {
                contentEl.innerHTML = newContent.content;
                contentEl.classList.add('no-csstransitions');

                if (breadcrumbEl) breadcrumbEl.innerHTML = newContent.breadcrumb;
                if (titleEl) titleEl.innerHTML = newContent.title;

                let headerEl = main.querySelector(_config.static.domIds.contentUpdateToH1);
                if (headerEl) {
                    const h1 = document.createElement('h1');
                    h1.innerHTML = headerEl.innerHTML;
                    for (const attr of headerEl.attributes) {
                        h1.setAttribute(attr.name, attr.value);
                    }

                    headerEl.parentNode.replaceChild(h1, headerEl);
                }

                _showContent();
            }, 200);
        }
    };

    /**
     * Remove old content above the map 
     * 
     */
    var _removeOldContent = function () {
        var nodesToRemove = document.querySelectorAll(_config.static.domIds.contentToRemove);
        nodesToRemove.forEach(function (node) { 
            if (node && node.parentNode) {
                node.parentNode.removeChild(node);
            }
        });
    };

    /**
     * Parse HTML string to extract new content
     * @param {string} data
     * @private
     */
    var _getNewContent = function (data) {
        var newContent = {
            breadcrumb: '',
            content: '',
            title: ''
        };

        // Use DOMParser to parse the incoming HTML string
        var parser = new DOMParser();
        var doc = parser.parseFromString(data, 'text/html');
        var main = doc.querySelector(_config.static.domIds.contentContainerMain);

        if (main) {
            var contentEl = main.querySelector(_config.static.domIds.contentContainerContent);
            if (contentEl) {
                var c = contentEl.innerHTML;
                // Replicate document.write replacement
                newContent.content = c.replace(/document\.write/g, 'document.querySelector("' + _config.static.domIds.contentContainerContent + '").insertAdjacentHTML("beforeend", ');
            }

            var breadcrumbEl = main.querySelector(_config.static.domIds.contentContainerBreadcrumb);
            if (breadcrumbEl) newContent.breadcrumb = breadcrumbEl.innerHTML;

            var titleEl = main.querySelector(_config.static.domIds.contentContainerTitle);
            if (titleEl) newContent.title = titleEl.innerHTML;
        }

        _showNotice();
        _track(data);
        _replaceContent(newContent);
    };

    /**
     * update share-specifics with new url
     * @private
     */
    var _setShareURL = function (url) {
        var canonical = document.querySelector('head link[rel="canonical"]');
        if (canonical) canonical.setAttribute('href', url);

        var ogUrl = document.querySelector('head meta[property="og:url"]');
        if (ogUrl) ogUrl.setAttribute('content', url);

        var recommendLink = document.querySelector('.share-mail a');
        if (recommendLink && recommendLink.getAttribute('href')) {
            var baseMail = recommendLink.getAttribute('href').split('?url=')[0];
            recommendLink.setAttribute('href', baseMail + '?url=' + url);
        }

        var shareLinks = document.querySelectorAll('.shariff-button a');
        shareLinks.forEach(function (link) {
            var href = link.getAttribute('href');
            if (!href) return;

            var qVars = ['u=', 'url='];
            for (var i = 0; i < qVars.length; i++) {
                if (href.indexOf(qVars[i]) !== -1) {
                    var prefix = href.split(qVars[i])[0] + qVars[i];
                    link.setAttribute('href', prefix + encodeURIComponent(url));
                    break;
                }
            }
        });
    };

    /**
     * @private
     */
    var _hideContent = function () {
        var el = document.querySelector(_config.static.domIds.contentContainerContent);
        if (el) {
            el.style.transition = 'opacity 200ms';
            el.style.opacity = 0.2;
        }
    };

    /**
     * @private
     */
    var _showContent = function () {
        var el = document.querySelector(_config.static.domIds.contentContainerContent);
        if (el) {
            el.style.transition = 'opacity 400ms';
            el.style.opacity = 1.0;
        }
    };

    /**
     * Show content ready notice
     * @private
     */
    var noticeTimer;
    var _showNotice = function () {
        _hideLoader();
        var notice = document.querySelector(_config.static.domIds.map.notice);
        if (notice) {
            notice.style.display = 'block';
            notice.style.opacity = 1;

            clearTimeout(noticeTimer);
            noticeTimer = setTimeout(function () {
                // Vanilla fadeOut
                notice.style.transition = 'opacity 500ms';
                notice.style.opacity = 0;
                setTimeout(function () { notice.style.display = 'none'; }, 500);
            }, 5000);
        }
    };

    /**
     * @private
     */
    var _hideNotice = function () {
        clearTimeout(noticeTimer);
        var notice = document.querySelector(_config.static.domIds.map.notice);
        if (notice) notice.style.display = 'none';
    };

    /**
     * @private
     */
    var _onNoticeClick = function () {
        var main = document.querySelector(_config.static.domIds.contentContainerMain);
        if (main) {
            var content = main.querySelector(_config.static.domIds.contentContainerContent);
            if (content) {
                var top = content.getBoundingClientRect().top + window.pageYOffset - 50;
                window.scrollTo({ top: top, behavior: 'smooth' });
            }
        }
    };

    /**
     * @private
     */
    var _initLoader = function () {
        var loader, notice;

        // Create the loader div
        loader = document.createElement("div");
        loader.classList.add("mapLoader");
        loader.id = _config.static.domIds.map.loader.replace(/#/, "");

        // Create the notice div
        notice = document.createElement("div");
        notice.classList.add("mapNotice");
        notice.id = _config.static.domIds.map.notice.replace(/#/, "");
        notice.textContent = SOSMapConfig.labelMoreInfoBelow;

        // Attach the event listener
        notice.addEventListener("click", _onNoticeClick);

        // Select parent and append both elements
        var mapMain = document.querySelector(_config.static.domIds.map.main);
        if (mapMain) {
            // .append() allows you to add multiple elements at once, similar to jQuery's chaining
            mapMain.append(loader, notice);
        }
    };

    /**
     * @private
     */
    var _showLoader = function (type) {
        var loader = document.querySelector(_config.static.domIds.map.loader);

        if (!loader) {
            _initLoader();
            loader = document.querySelector(_config.static.domIds.map.loader);
        }
        _hideNotice();

        var label = SOSMapConfig.labelLoadInfo;
        if (loader) {
            loader.textContent = label;
            loader.style.display = 'block';
        }
    };

    /**
     * @private
     */
    var _hideLoader = function () {
        var loader = document.querySelector(_config.static.domIds.map.loader);
        if (loader) loader.style.display = 'none';
    };

    /**
     * Fetch logic replacing $.get
     * @private
     */
    var _get = function (url, type) {
        if (_firstLoad === true || actURL === url) {
            _firstLoad = false;
            return;
        }

        actURL = url;
        _hideContent();
        _showLoader(type);

        // Handle request aborting
        if (_contentRequest) {
            _contentRequest.abort();
        }
        _contentRequest = new AbortController();

        url = url.toLowerCase();
        _setShareURL(url);

        window.setTimeout(function () {
            var fetchUrl = url;
            if (_config.static.baseUrl.indexOf('viazenetti.de') !== -1) {
                fetchUrl = _config.static.baseUrl + '/gateway.php?url=' + url;
            }

            fetch(fetchUrl, {
                signal: _contentRequest.signal,
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
                .then(function (response) {
                    if (!response.ok) throw new Error('Network response was not ok');
                    return response.text();
                })
                .then(function (data) {
                    _removeOldContent();
                    _getNewContent(data);
                })
                .catch(function (error) {
                    if (error.name === 'AbortError') return;
                    _hideLoader();
                    _showContent();
                });
        }, 100);
    };

    /**
     * @private
     */
    var _update = function (type, id) {
        var item = null;
        var emergencyId = null;

        if (type === 'continent') {
            item = Continents.getContinent(id, true);
        } else if (type === 'country') {
            item = Countries.getCountry(id, true);
            emergencyId = parseInt(id);
        } else if (type === 'village') {
            item = Villages.getVillage(id, true);
            if (item) emergencyId = parseInt(item.country);
        } else if (type === 'project') {
            item = Projects.getProject(id, true);
        }

        if (typeof Projects !== 'undefined') {
            Projects.showEmergencyInCountry(emergencyId);
        }

        if (item && item.url) {
            _get(item.url, type);
        }

        if (type === 'world') {
            _get(_config.static.worldBaseUrl, type);
        }
    };

    return {
        update: _update
    };
})();

window.initSOSMap = function () {
    Core.init();
};

(function () {
    function loadScriptOnce() {
        if (document.querySelector(`script[src="${SOSMapConfig.scriptURL}"]`)) {
            return;
        }
        const script = document.createElement('script');
        script.src = SOSMapConfig.scriptURL;
        script.async = true;
        document.head.appendChild(script);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', loadScriptOnce);
    } else {
        loadScriptOnce();
    }
})();